'use client';

import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Coins, Crown, Loader2, ShieldAlert } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './Dialog';

/** Luminância relativa — usa pra decidir se botão tem texto preto ou branco. */
function isLightColor(hex: string): boolean {
  const h = hex.replace('#', '');
  if (h.length !== 6) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // luminância perceptual aproximada
  return (r * 0.299 + g * 0.587 + b * 0.114) > 170;
}

// ────────────────────────────────────────────────────────────────
//  Types
// ────────────────────────────────────────────────────────────────

export type ConfirmTone = 'cyan' | 'danger' | 'warning' | 'gold';

export type ConfirmOptions = {
  title: string;
  description?: string;
  /** Conteudo extra em JSX (ex: destaque de preco/nome do personagem) */
  body?: React.ReactNode;

  /** Label do botao de confirmacao. Default: "Confirmar". */
  confirmLabel?: string;
  /** Label do botao de cancelar. Default: "Cancelar". */
  cancelLabel?: string;

  /** Tom visual do dialog (cor do header/botao). Default: 'cyan'. */
  tone?: ConfirmTone;

  /** Override de cor (hex). Sobrescreve o tone. */
  accentColor?: string;

  /** Icone custom (substitui o default do tone). */
  icon?: React.ReactNode;
};

type InternalState = ConfirmOptions & { open: boolean };

type Ctx = {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
};

// ────────────────────────────────────────────────────────────────
//  Tone palette
// ────────────────────────────────────────────────────────────────

const TONE_STYLES: Record<ConfirmTone, {
  color: string;
  bgSoft: string;
  border: string;
  glow: string;
  icon: React.ReactNode;
}> = {
  cyan: {
    color: '#00E1D7',
    bgSoft: 'rgba(0, 225, 215, 0.12)',
    border: 'rgba(0, 225, 215, 0.35)',
    glow: 'rgba(0, 225, 215, 0.30)',
    icon: <CheckCircle2 size={24} />,
  },
  danger: {
    color: '#fa0000',
    bgSoft: 'rgba(250, 0, 0, 0.12)',
    border: 'rgba(250, 0, 0, 0.4)',
    glow: 'rgba(250, 0, 0, 0.32)',
    icon: <ShieldAlert size={24} />,
  },
  warning: {
    color: '#facc15',
    bgSoft: 'rgba(250, 204, 21, 0.12)',
    border: 'rgba(250, 204, 21, 0.4)',
    glow: 'rgba(250, 204, 21, 0.28)',
    icon: <AlertTriangle size={24} />,
  },
  gold: {
    color: '#FFD700',
    bgSoft: 'rgba(255, 215, 0, 0.12)',
    border: 'rgba(255, 215, 0, 0.4)',
    glow: 'rgba(255, 215, 0, 0.3)',
    icon: <Crown size={24} />,
  },
};

// ────────────────────────────────────────────────────────────────
//  Context + hook
// ────────────────────────────────────────────────────────────────

const ConfirmContext = createContext<Ctx | null>(null);

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm deve ser usado dentro de <ConfirmDialogProvider />');
  return ctx.confirm;
}

// ────────────────────────────────────────────────────────────────
//  Provider
// ────────────────────────────────────────────────────────────────

export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<InternalState | null>(null);
  const [busy, setBusy] = useState(false);
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);

  const settle = useCallback((ok: boolean) => {
    resolverRef.current?.(ok);
    resolverRef.current = null;
    // fade out antes de limpar
    setState((s) => (s ? { ...s, open: false } : null));
    setTimeout(() => {
      setState(null);
      setBusy(false);
    }, 160);
  }, []);

  const confirm = useCallback((opts: ConfirmOptions) => {
    // Se ja houver um confirm aberto, resolve false (cancela o anterior).
    if (resolverRef.current) {
      resolverRef.current(false);
      resolverRef.current = null;
    }
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setState({ ...opts, open: true });
    });
  }, []);

  const tone: ConfirmTone = state?.tone ?? 'cyan';
  const basePalette = TONE_STYLES[tone];
  // Se accentColor foi passado, derivar TODA a paleta dele (não só a cor).
  // Assim botão, glow, borda e bg ficam harmonizados com o plano/contexto.
  const color = state?.accentColor ?? basePalette.color;
  const palette = state?.accentColor
    ? {
        color,
        bgSoft: `${color}1f`,
        border: `${color}66`,
        glow: `${color}59`,
        icon: basePalette.icon,
      }
    : basePalette;
  const icon = state?.icon ?? palette.icon;
  // Texto preto pra cores muito claras (amarelo, dourado, ciano puro); branco pro resto
  const useBlackText = state?.accentColor
    ? isLightColor(color)
    : (tone === 'warning' || tone === 'gold');

  const handleCancel = () => settle(false);
  const handleConfirm = () => {
    setBusy(true);
    settle(true);
  };

  return (
    <ConfirmContext.Provider value={{ confirm }}>
      {children}

      <Dialog open={!!state?.open} onOpenChange={(v) => { if (!v) handleCancel(); }}>
        <DialogContent
          onClose={handleCancel}
          className="relative w-[min(480px,92vw)] overflow-hidden rounded-[1.4rem] border bg-[#0a1218]/95 text-white backdrop-blur-xl"
          style={{
            borderColor: palette.border,
            boxShadow: `0 0 60px ${palette.glow}`,
          }}
        >
          {/* Glow ambiente */}
          <div className="pointer-events-none absolute -inset-10 -z-10">
            <div
              className="absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl"
              style={{ background: color, opacity: 0.12 }}
            />
          </div>

          {/* Header com icone */}
          <DialogHeader className="flex-row items-start gap-4 text-left">
            <div
              className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center border"
              style={{
                background: palette.bgSoft,
                color,
                borderColor: palette.border,
              }}
            >
              {icon}
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl font-extrabold tracking-tight" style={{ color }}>
                {state?.title}
              </DialogTitle>
              {state?.description && (
                <DialogDescription className="text-white/70 mt-1.5 leading-relaxed">
                  {state.description}
                </DialogDescription>
              )}
            </div>
          </DialogHeader>

          {/* Body opcional */}
          {state?.body && (
            <div className="mt-1 text-sm text-white/80">
              {state.body}
            </div>
          )}

          {/* Actions */}
          <DialogFooter className="mt-2 gap-2">
            <button
              type="button"
              className="px-5 py-2.5 rounded-xl text-sm font-bold text-white/75 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition cursor-pointer disabled:opacity-50"
              disabled={busy}
              onClick={handleCancel}
            >
              {state?.cancelLabel ?? 'Cancelar'}
            </button>
            <button
              type="button"
              className="px-6 py-2.5 rounded-xl text-sm font-extrabold transition cursor-pointer disabled:opacity-50 flex items-center gap-2"
              disabled={busy}
              onClick={handleConfirm}
              style={{
                background: `linear-gradient(135deg, ${color}, ${color}dd)`,
                color: useBlackText ? '#000' : '#fff',
                boxShadow: `0 0 20px ${palette.glow}`,
              }}
            >
              {busy && <Loader2 size={14} className="animate-spin" />}
              {state?.confirmLabel ?? 'Confirmar'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}

// ────────────────────────────────────────────────────────────────
//  Helper: linha de destaque (preco, nome, etc) pra usar no `body`
// ────────────────────────────────────────────────────────────────

export function ConfirmHighlight({
  icon, label, value, color = '#00E1D7',
}: {
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  color?: string;
}) {
  return (
    <div
      className="flex items-center gap-3 rounded-xl border px-4 py-3 mt-3"
      style={{ borderColor: `${color}30`, background: `${color}0a` }}
    >
      {icon && (
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ background: `${color}18`, color }}
        >
          {icon}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold text-white/45 uppercase tracking-wider">{label}</p>
        <p className="text-base font-extrabold truncate" style={{ color }}>{value}</p>
      </div>
    </div>
  );
}

// Reexport util (pra nao precisar importar lucide em todo uso)
export { Coins };
